import { useContext, useEffect, useCallback, useState } from 'react'
import { Stack, MessageBar, MessageBarType } from '@fluentui/react'
import { AppStateContext, DashboardData } from '../../state/AppProvider'
import { DashboardFilters } from '../../api/models'
import {
  getConversationCount,
  getFeedbackSummary,
  getFeedbackOverTime,
  getQuestionsByPlan,
  getTopQuestions,
  getActiveUsers,
  getConversationsTimeline,
  getFilterPlans,
  getFilterUsers,
} from '../../api/api'
import FilterBar from '../../components/filters/FilterBar'
import SummaryCards from '../../components/charts/SummaryCards'
import FeedbackChart from '../../components/charts/FeedbackChart'
import FeedbackOverTimeChart from '../../components/charts/FeedbackOverTimeChart'
import QuestionsByPlanChart from '../../components/charts/QuestionsByPlanChart'
import ConversationsTimeline from '../../components/charts/ConversationsTimeline'
import TopQuestionsTable from '../../components/charts/TopQuestionsTable'
import ActiveUsersTable from '../../components/charts/ActiveUsersTable'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import KrTracker from '../../components/kr/KrTracker'
import { isKrEnvironment } from '../../components/kr/krEnvironment'

const Dashboard = () => {
  const appStateContext = useContext(AppStateContext)
  const state = appStateContext?.state
  const dispatch = appStateContext?.dispatch

  const [filters, setFilters] = useState<DashboardFilters>(state?.filters || {})
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(state?.dashboardData || null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchDashboardData = useCallback(async (currentFilters: DashboardFilters) => {
    setIsLoading(true)
    setError(null)

    try {
      const [conversationCount, feedbackSummary, feedbackOverTime, questionsByPlan, topQuestions, activeUsers, timeline] =
        await Promise.all([
          getConversationCount(currentFilters),
          getFeedbackSummary(currentFilters),
          getFeedbackOverTime(currentFilters),
          getQuestionsByPlan(currentFilters),
          getTopQuestions(currentFilters),
          getActiveUsers(currentFilters),
          getConversationsTimeline(currentFilters),
        ])

      const data: DashboardData = {
        conversationCount,
        feedbackSummary,
        feedbackOverTime,
        questionsByPlan,
        topQuestions,
        activeUsers,
        timeline,
      }
      setDashboardData(data)

      if (dispatch) {
        dispatch({ type: 'SET_DASHBOARD_DATA', payload: { data, filters: currentFilters } })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!state?.dashboardData || state.dashboardFilters !== state.filters) {
      fetchDashboardData(filters)
    }
    // Load filter options if not cached
    if (dispatch && state?.plans.length === 0) {
      getFilterPlans().then((plans) => dispatch({ type: 'SET_PLANS', payload: plans })).catch(() => {})
      getFilterUsers().then((users) => dispatch({ type: 'SET_USERS', payload: users })).catch(() => {})
    }
  }, [])

  const handleApplyFilters = () => {
    if (dispatch) {
      dispatch({ type: 'SET_FILTERS', payload: filters })
    }
    fetchDashboardData(filters)
  }

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      {isKrEnvironment() && <KrTracker />}

      {/* Filter Bar */}
      <FilterBar
        filters={filters}
        plans={state?.plans || []}
        users={state?.users || []}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        isLoading={isLoading}
      />

      {/* Error Message */}
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {/* Loading State */}
      {isLoading && <LoadingSpinner label="Loading dashboard data..." />}

      {/* Dashboard Content */}
      {!isLoading && dashboardData && (
        <Stack tokens={{ childrenGap: 20 }}>
          {/* Summary Cards */}
          <SummaryCards data={dashboardData} />

          {/* Charts Row: Weekly Feedback + Questions by Category */}
          <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { flexWrap: 'wrap' } }}>
            <FeedbackOverTimeChart data={dashboardData.feedbackOverTime} />
            <QuestionsByPlanChart data={dashboardData.questionsByPlan} />
          </Stack>

          {/* Feedback Doughnut + Conversations Timeline */}
          <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { flexWrap: 'wrap' } }}>
            <FeedbackChart data={dashboardData.feedbackSummary} />
            <ConversationsTimeline data={dashboardData.timeline} />
          </Stack>

          {/* Active Users Table */}
          <ActiveUsersTable data={dashboardData.activeUsers} />

          {/* Most Repeated Questions */}
          <TopQuestionsTable data={dashboardData.topQuestions} />
        </Stack>
      )}
    </Stack>
  )
}

export default Dashboard
